import type { PublicPlan } from "@brandex/api-client";
import { formatTaka, type MessageKey } from "@brandex/i18n";
import { ACCENT_KEYS, accentHex, Badge, Button, ChoiceChips, Field, Input, MultiChoiceChips, Textarea, cn } from "@brandex/ui";
import { Check, Crosshair, ExternalLink, Globe, MapPin, Plus, Search, Trash2 } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { useBrandex, useT } from "../context";
import {
  LANGUAGES,
  SHOOT_TYPES,
  SLOT_MINUTES,
  SPECIALTIES,
  WEEKDAYS,
  customLines,
  emptyChamber,
  type ChamberDraft,
  type OnboardingDraft,
  type PackageDraft,
  type StepErrors,
} from "./draft";
import { mapsPinUrl, mapsSearchUrl, parseLocation } from "./location";

export interface StepProps {
  draft: OnboardingDraft;
  update: (patch: Partial<OnboardingDraft>) => void;
  errors: StepErrors;
}

/** A labelled group of chips with its error, wired for assistive tech. */
function Group({ label, error, children }: { label: string; error?: string | undefined; children: (ids: { describedBy?: string; invalid: boolean }) => ReactNode }) {
  const id = useId();
  return (
    <div className="grid gap-2">
      <span className="text-sm font-medium leading-none">{label}</span>
      {children({ ...(error !== undefined ? { describedBy: id } : {}), invalid: error !== undefined })}
      {error !== undefined ? <p id={id} role="alert" className="text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  );
}

function useErr(errors: StepErrors) {
  const t = useT();
  return (field: string) => {
    const key = errors[field];
    return key === undefined ? undefined : t(key);
  };
}

export function PracticeStep({ draft, update, errors, askName = false }: StepProps & { askName?: boolean }) {
  const t = useT();
  const err = useErr(errors);
  return (
    <div className="grid gap-5">
      {askName || err("full_name") !== undefined ? (
        <Field label={t("onboarding.fullName")} hint={t("onboarding.fullNameHint")} error={err("full_name")}>
          <Input autoComplete="name" maxLength={100} value={draft.fullName} onChange={(e) => update({ fullName: e.target.value })} />
        </Field>
      ) : null}
      <Group label={t("onboarding.specialty")} error={err("specialty")}>
        {(a) => (
          <ChoiceChips
            label={t("onboarding.specialty")}
            value={draft.specialty ?? undefined}
            onChange={(specialty) => update({ specialty })}
            options={SPECIALTIES.map((s) => ({ value: s, label: t(`specialty.${s}` as MessageKey) }))}
            {...a}
          />
        )}
      </Group>
      <Field label={t("onboarding.designation")} hint={t("onboarding.designationHint")} error={err("designation")}>
        <Input value={draft.designation} maxLength={160} onChange={(e) => update({ designation: e.target.value })} />
      </Field>
      <Field label={t("onboarding.registration")} hint={t("onboarding.registrationHint")} error={err("registration_no")}>
        <Input value={draft.registrationNo} maxLength={40} onChange={(e) => update({ registrationNo: e.target.value })} />
      </Field>
      <Group label={t("onboarding.languages")} error={err("languages")}>
        {(a) => (
          <MultiChoiceChips
            label={t("onboarding.languages")}
            values={draft.languages}
            onChange={(languages) => update({ languages })}
            options={LANGUAGES.map((l) => ({ value: l, label: t(`language.${l}` as MessageKey) }))}
            {...a}
          />
        )}
      </Group>
    </div>
  );
}

export function QualificationsStep({ draft, update, errors }: StepProps) {
  const t = useT();
  const err = useErr(errors);
  const setDegree = (i: number, patch: Partial<OnboardingDraft["degrees"][number]>) =>
    update({ degrees: draft.degrees.map((g, j) => (j === i ? { ...g, ...patch } : g)) });
  return (
    <div className="grid gap-5">
      <fieldset className="grid gap-3">
        <legend className="mb-2 text-sm font-medium">{t("onboarding.degrees")}</legend>
        {draft.degrees.map((g, i) => (
          <div key={i} className="grid gap-3 rounded-[var(--radius-lg)] border border-border p-3 sm:grid-cols-[1.4fr_1.4fr_0.7fr_auto] sm:items-end">
            <Field label={t("onboarding.degreeTitle", { n: i + 1 })} error={err(`degrees.${i}.title`)}>
              <Input value={g.title} maxLength={120} placeholder="MBBS" onChange={(e) => setDegree(i, { title: e.target.value })} />
            </Field>
            <Field label={t("onboarding.degreeInstitution")} error={err(`degrees.${i}.institution`)}>
              <Input value={g.institution} maxLength={160} onChange={(e) => setDegree(i, { institution: e.target.value })} />
            </Field>
            <Field label={t("onboarding.degreeYear")} error={err(`degrees.${i}.year`)}>
              <Input inputMode="numeric" maxLength={4} value={g.year} onChange={(e) => setDegree(i, { year: e.target.value.replace(/\D/g, "") })} />
            </Field>
            <Button type="button"
              variant="ghost"
              size="icon"
              aria-label={t("onboarding.removeDegree", { n: i + 1 })}
              disabled={draft.degrees.length === 1}
              onClick={() => update({ degrees: draft.degrees.filter((_, j) => j !== i) })}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        ))}
        {draft.degrees.length < 10 ? (
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => update({ degrees: [...draft.degrees, { title: "", institution: "", year: "" }] })}>
              <Plus className="h-4 w-4" aria-hidden />
              {t("onboarding.addDegree")}
            </Button>
          </div>
        ) : null}
      </fieldset>
      <Field label={t("onboarding.bio")} hint={t("onboarding.bioHint", { count: draft.bio.trim().length })} error={err("bio")}>
        <Textarea rows={4} maxLength={600} value={draft.bio} onChange={(e) => update({ bio: e.target.value })} />
      </Field>
    </div>
  );
}

export function ChambersStep({ draft, update, errors }: StepProps) {
  const t = useT();
  const err = useErr(errors);
  const setChamber = (i: number, patch: Partial<ChamberDraft>) =>
    update({ chambers: draft.chambers.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <div className="grid gap-5">
      {draft.chambers.map((c, i) => (
        <fieldset key={i} className="grid gap-4 rounded-[var(--radius-lg)] border border-border p-4">
          <div className="flex items-center justify-between">
            <legend className="font-semibold">{t("onboarding.chamber", { n: i + 1 })}</legend>
            {draft.chambers.length > 1 ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => update({ chambers: draft.chambers.filter((_, j) => j !== i) })}>
                <Trash2 className="h-4 w-4" aria-hidden />
                {t("onboarding.removeChamber", { n: i + 1 })}
              </Button>
            ) : null}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("onboarding.chamberName")} error={err(`chambers.${i}.name`)}>
              <Input value={c.name} maxLength={120} onChange={(e) => setChamber(i, { name: e.target.value })} />
            </Field>
            <Field label={t("onboarding.chamberAddress")} error={err(`chambers.${i}.address`)}>
              <Input value={c.address} maxLength={300} onChange={(e) => setChamber(i, { address: e.target.value })} />
            </Field>
          </div>
          <LocationField
            n={i + 1}
            chamber={c}
            error={err(`chambers.${i}.location`)}
            onChange={(location) => setChamber(i, { location })}
          />
          <Group label={t("onboarding.days")} error={err(`chambers.${i}.days`)}>
            {(a) => (
              <MultiChoiceChips
                label={`${t("onboarding.chamber", { n: i + 1 })} · ${t("onboarding.days")}`}
                values={c.days}
                onChange={(days) => setChamber(i, { days })}
                options={WEEKDAYS.map((d) => ({ value: d, label: t(`day.${d}` as MessageKey) }))}
                {...a}
              />
            )}
          </Group>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("onboarding.from")}>
              <Input type="time" step={300} value={c.opens} onChange={(e) => setChamber(i, { opens: e.target.value })} />
            </Field>
            <Field label={t("onboarding.to")} error={err(`chambers.${i}.closes`)}>
              <Input type="time" step={300} value={c.closes} onChange={(e) => setChamber(i, { closes: e.target.value })} />
            </Field>
          </div>
          <Group label={t("onboarding.minutes")}>
            {() => (
              <ChoiceChips
                label={`${t("onboarding.chamber", { n: i + 1 })} · ${t("onboarding.minutes")}`}
                value={c.slotMinutes}
                onChange={(slotMinutes) => setChamber(i, { slotMinutes })}
                options={SLOT_MINUTES.map((m) => ({ value: m, label: t("onboarding.minutesValue", { n: m }) }))}
              />
            )}
          </Group>
        </fieldset>
      ))}
      {draft.chambers.length < 10 ? (
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => update({ chambers: [...draft.chambers, emptyChamber()] })}>
            <Plus className="h-4 w-4" aria-hidden />
            {t("onboarding.addChamber")}
          </Button>
        </div>
      ) : null}
      <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-lg)] border border-border p-4 text-sm">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--color-primary)]" checked={draft.autoConfirm} onChange={(e) => update({ autoConfirm: e.target.checked })} />
        <span>{t("onboarding.autoConfirm")}</span>
      </label>
    </div>
  );
}

/**
 * Where the chamber is on the map, for "Get directions" on the website.
 * Paste a Google Maps link, type coordinates, or use this device's location
 * while at the chamber. Optional.
 */
function LocationField({ n, chamber, error, onChange }: { n: number; chamber: ChamberDraft; error: string | undefined; onChange: (location: string) => void }) {
  const t = useT();
  const { platform } = useBrandex();
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<MessageKey | null>(null);
  const raw = chamber.location;
  const parsed = parseLocation(raw);
  const canLocate = typeof navigator !== "undefined" && "geolocation" in navigator;
  const locate = () => {
    setGeoError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        onChange(`${Math.round(pos.coords.latitude * 1e6) / 1e6}, ${Math.round(pos.coords.longitude * 1e6) / 1e6}`);
      },
      (e) => {
        setLocating(false);
        setGeoError(e.code === e.PERMISSION_DENIED ? "onboarding.location.denied" : "onboarding.location.unavailable");
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  };
  return (
    <div className="grid gap-2">
      <Field
        label={t("onboarding.location")}
        // Coordinates as strings: number formatting would round them (and use Bangla digits).
        hint={parsed.kind === "ok" ? t("onboarding.location.set", { lat: String(parsed.point.lat), lng: String(parsed.point.lng) }) : t("onboarding.location.hint")}
        // The value is re-read as it changes: a fixed value clears an earlier error.
        error={(parsed.kind === "ok" || parsed.kind === "empty" ? undefined : error) ?? (geoError === null ? undefined : t(geoError))}
      >
        <Input
          value={raw}
          maxLength={2000}
          inputMode="url"
          placeholder={t("onboarding.location.placeholder")}
          aria-label={`${t("onboarding.chamber", { n })} · ${t("onboarding.location")}`}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
      <div className="flex flex-wrap gap-2">
        {canLocate ? (
          <Button type="button" variant="outline" size="sm" loading={locating} onClick={locate}>
            <Crosshair className="h-4 w-4" aria-hidden />
            {t("onboarding.location.here")}
          </Button>
        ) : null}
        <Button type="button" variant="ghost" size="sm" onClick={() => void platform.openExternal(mapsSearchUrl(chamber.name, chamber.address))}>
          <Search className="h-4 w-4" aria-hidden />
          {t("onboarding.location.find")}
        </Button>
        {parsed.kind === "ok" ? (
          <>
            <Button type="button" variant="ghost" size="sm" onClick={() => void platform.openExternal(mapsPinUrl(parsed.point))}>
              <MapPin className="h-4 w-4" aria-hidden />
              {t("onboarding.location.check")}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")}>
              {t("onboarding.location.clear")}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function PackageStep({ draft, update, errors, plans }: StepProps & { plans: readonly PublicPlan[] }) {
  const t = useT();
  const { locale } = useBrandex();
  const err = useErr(errors);
  const name = (n: { en: string; bn: string }) => (locale === "bn" && n.bn ? n.bn : n.en);
  const { perItem, addOns } = customLines(plans);
  const custom: Extract<PackageDraft, { kind: "custom" }> =
    draft.package.kind === "custom" ? draft.package : { kind: "custom", quantities: {}, addOns: [], note: "" };
  const setCustom = (patch: Partial<typeof custom>) => update({ package: { ...custom, ...patch } });
  return (
    <div className="grid gap-4">
      <p className="text-sm text-muted-foreground">{t("onboarding.packageIntro")}</p>
      {err("package") !== undefined ? <p role="alert" className="text-sm font-medium text-destructive">{err("package")}</p> : null}
      {plans.length === 0 ? <p className="text-sm">{t("onboarding.noPlans")}</p> : null}
      <div role="radiogroup" aria-label={t("onboarding.steps.package")} className="grid gap-3">
        {plans.map((p) => {
          const selected = draft.package.kind === "plan" && draft.package.planId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => update({ package: { kind: "plan", planId: p.id } })}
              className={cn(
                "grid gap-2 rounded-[var(--radius-lg)] border border-border p-4 text-left transition-colors hover:border-primary/60",
                selected && "border-primary bg-primary/10 shadow-[inset_0_0_0_1px_var(--color-primary)]",
              )}
            >
              <span className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-display text-lg font-semibold">{name(p.name)}</span>
                <span className="font-display text-xl font-bold text-azure-500">
                  {formatTaka(locale, p.monthly_price_paisa)} <span className="text-sm font-normal text-muted-foreground">{t("package.perMonth")}</span>
                </span>
              </span>
              {name(p.description) ? <span className="text-sm text-muted-foreground">{name(p.description)}</span> : null}
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone={p.includes_website ? "success" : "neutral"}>
                  <Globe className="mr-1 inline h-3 w-3" aria-hidden />
                  {t(p.includes_website ? "onboarding.includesWebsite" : "onboarding.noWebsite")}
                </Badge>
                {selected ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                    <Check className="h-3.5 w-3.5" aria-hidden />
                    {t("onboarding.selected")}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
        <div
          className={cn(
            "grid gap-3 rounded-[var(--radius-lg)] border border-border p-4",
            draft.package.kind === "custom" && "border-primary bg-primary/10 shadow-[inset_0_0_0_1px_var(--color-primary)]",
          )}
        >
          <button type="button" role="radio" aria-checked={draft.package.kind === "custom"} onClick={() => setCustom({})} className="grid gap-1 text-left">
            <span className="flex items-center justify-between gap-2">
              <span className="font-display text-lg font-semibold">{t("package.custom")}</span>
              <span className="text-sm text-muted-foreground">{t("package.toQuote")}</span>
            </span>
            <span className="text-sm text-muted-foreground">{t("package.customHint")}</span>
          </button>
          {draft.package.kind === "custom" ? (
            <div className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                {perItem.map((l) => (
                  <Field key={l.component_id} label={name(l.name)}>
                    <Input
                      inputMode="numeric"
                      value={String(custom.quantities[l.component_id] ?? l.quantity)}
                      onChange={(e) => setCustom({ quantities: { ...custom.quantities, [l.component_id]: Number(e.target.value.replace(/\D/g, "") || 0) } })}
                    />
                  </Field>
                ))}
              </div>
              {addOns.length > 0 ? (
                <MultiChoiceChips
                  label={t("package.custom")}
                  values={custom.addOns}
                  onChange={(ids) => setCustom({ addOns: ids })}
                  options={addOns.map((l) => ({ value: l.component_id, label: t("onboarding.addOn", { name: name(l.name) }) }))}
                />
              ) : null}
              <Field label={t("package.customNote")}>
                <Textarea rows={2} maxLength={2000} value={custom.note} onChange={(e) => setCustom({ note: e.target.value })} />
              </Field>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function BrandStep({ draft, update }: StepProps) {
  const t = useT();
  return (
    <div className="grid gap-6">
      <Group label={t("onboarding.accent")}>
        {() => (
          <ChoiceChips
            label={t("onboarding.accent")}
            value={draft.accent}
            onChange={(accent) => update({ accent })}
            options={ACCENT_KEYS.map((k) => ({
              value: k,
              ariaLabel: t(`accent.${k}` as MessageKey),
              label: (
                <>
                  <span aria-hidden className="h-4 w-4 rounded-full ring-2 ring-white/70" style={{ background: accentHex(k) }} />
                  <span>{t(`accent.${k}` as MessageKey)}</span>
                </>
              ),
            }))}
          />
        )}
      </Group>
      <div aria-hidden className="overflow-hidden rounded-[var(--radius-lg)] border border-border">
        <div className="flex items-center gap-3 p-4 text-white" style={{ background: `linear-gradient(120deg, ${accentHex(draft.accent)}, ${accentHex(draft.accent)}cc)` }}>
          <span className="grid h-10 w-10 place-items-center rounded-full bg-white/20 font-semibold">{initials(draft.fullName)}</span>
          <span className="grid">
            <span className="font-display font-semibold">{draft.fullName || "Dr."}</span>
            <span className="text-xs opacity-85">{draft.designation}</span>
          </span>
        </div>
      </div>
      <Group label={t("onboarding.shootType")}>
        {() => (
          <ChoiceChips
            label={t("onboarding.shootType")}
            value={draft.shootType}
            onChange={(shootType) => update({ shootType })}
            options={SHOOT_TYPES.map((s) => ({ value: s, label: t(`shoot.${s}` as MessageKey) }))}
          />
        )}
      </Group>
    </div>
  );
}

/** "Dr. Ayesha Rahman" → "AR" (honorifics skipped), matching the API. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => w.length > 0 && !["dr", "prof", "ডা", "ডাঃ"].includes(w.toLowerCase().replace(/\.$/, "")))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}
